import type {
    ObjectShorthandValue,
    ProfessionalTileProps,
} from '@archipro-design/aria';
import { useRef } from 'react';
import type { AdvertCreative } from '@archipro-website/tracker';
import { useAdEventHandler, useLogImpression } from '@archipro-website/tracker';
import { Tile } from '@rocco/ui/tile-shared/components/Tile';
import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import type { ProfessionalTileViewModel } from '@rocco/ui/tile-professional/view-models/professional';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';

type TileProps = ObjectShorthandValue<ProfessionalTileProps> & {
    advertCreative: AdvertCreative;
};

/**
 * ProfessionalAdvertTile component renders a tile for professional advertisements.
 * It handles ad click events and logs impressions.
 *
 * @param {Object} props - The properties for the component.
 * @param {Function} props.onClick - Callback function for click events.
 * @param {AdvertCreative} props.advertCreative - The advertisement creative data.
 * @param {Object} rest - Additional properties for the component.
 */
const ProfessionalAdvertTile = ({
    onClick,
    advertCreative,
    ...rest
}: TileProps) => {
    const ref = useRef<HTMLDivElement | null>(null);
    const tileRef = useRef<HTMLDivElement | null>(null);
    const { onClick: adOnClick } = useAdEventHandler(advertCreative, ref);
    const { handleFollow, isInDesignBoard } = useHandleFollow();

    /**
     * Handles the click event for the tile, triggering ad click tracking.
     */
    const tileOnClick = () => {
        adOnClick('Ad_CTAClick');
        onClick?.();
    };

    useLogImpression({
        data: rest.impression,
        ref: tileRef,
    });

    const tile: TileViewModel<ProfessionalTileViewModel> = {
        model: {
            id: advertCreative.parent.id,
            title: advertCreative.title,
            image: {
                src: (rest.image[0] as { src: string })?.src || '',
                alt: advertCreative.title,
            },
            link: advertCreative.cta.href,
            video: [],
            hasAwards: false,
            logo: {
                image: {
                    src:
                        (rest.logo as { image: { src: string; alt: string } })
                            ?.image?.src || '',
                    alt:
                        (rest.logo as { image: { src: string; alt: string } })
                            ?.image?.alt || '',
                },
                background: advertCreative.professional.logoBackgroundColor,
            },
            category: rest.category as string,
            services: ['Sponsored', ...(rest.services as string[])],
            location: rest.location as string,
            memberSince: 0,
        },
        isFollowed: isInDesignBoard(
            advertCreative.parent.id,
            'ProfessionalPin'
        ),
        onFollow: (pinId, pinType) => handleFollow({ id: pinId }, pinType),
    };

    return (
        <div ref={tileRef} onClick={tileOnClick}>
            <Tile tile={tile} />
        </div>
    );
};

export default ProfessionalAdvertTile;
